import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Hammer, ImagePlus, X, Sparkles } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "automobile",   label: "Automobile Engineering" },
  { value: "crafts",       label: "Crafts & Artisan" },
  { value: "electronics",  label: "Electronics & Robotics" },
  { value: "fashion",      label: "Fashion Design" },
  { value: "architecture", label: "Architecture & Furniture" },
  { value: "other",        label: "Other Builds" },
];

const MakerNew = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "",
    story: "",
    category: "crafts",
    country: user?.country || "",
    region: "",
    funding_goal: "",
    video_url: "",
  });
  const [imageUrls, setImageUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (imageUrls.length >= 6) {
      toast.error("Max 6 photos");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data?.url || data?.image_url || data?.file_url;
      if (!url) throw new Error("no url");
      setImageUrls((prev) => [...prev, url]);
    } catch (err) {
      toast.error("Upload failed — try a smaller image");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImg = (i) => setImageUrls((prev) => prev.filter((_, idx) => idx !== i));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please sign in first");
      return;
    }
    if (form.story.trim().length < 20) {
      toast.error("Tell us a bit more about your work (20+ characters)");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        story: form.story.trim(),
        category: form.category,
        country: form.country.trim(),
        region: form.region.trim() || null,
        image_urls: imageUrls,
        video_url: form.video_url.trim() || null,
        funding_goal: Number(form.funding_goal) || 0,
      };
      const { data } = await api.post("/makers/showcases", payload);
      toast.success("Your work is now on the exhibition floor 🎉");
      navigate(`/makers/${data.showcase_id}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not publish — please try again.";
      toast.error(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-5" data-testid="makers-new-needs-auth">
        <div className="bg-white rounded-3xl p-10 max-w-md text-center border border-stone-200">
          <Hammer className="w-12 h-12 mx-auto text-stone-300 mb-4" />
          <h2 className="font-display text-2xl mb-2">Sign in to submit your work</h2>
          <p className="text-sm text-stone-500 mb-6">
            We need a verified AfriMarket account so sponsors can reach you safely.
          </p>
          <a
            href={`/login?next=${encodeURIComponent("/makers/new")}`}
            className="inline-block px-6 py-2.5 rounded-full bg-stone-900 text-white text-sm font-bold"
          >
            Sign in
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="makers-new-page">
      <div className="max-w-2xl mx-auto px-5 py-10">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-semibold text-stone-500 mb-3">
          <Sparkles className="w-3 h-3" /> Makers Exhibition
        </div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight">Show the world what you build.</h1>
        <p className="text-stone-500 text-sm mt-2">
          Photos sell it. Story closes it. Tell us about the craft and we'll put it in front of every sponsor on the platform.
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-5">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-stone-700">Title</label>
            <input
              type="text"
              required
              minLength={4}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="makers-input-title"
              placeholder="e.g. Custom solar-powered tuk-tuk frame"
              className="mt-1 w-full rounded-xl border border-stone-300 px-4 py-3 text-sm focus:border-stone-900 focus:ring-1 focus:ring-stone-900 outline-none"
            />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-stone-700">Category</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="makers-input-category"
              className="mt-1 w-full rounded-xl border border-stone-300 px-4 py-3 text-sm focus:border-stone-900 focus:ring-1 focus:ring-stone-900 outline-none bg-white"
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-700">Country</label>
              <input
                type="text"
                required
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                data-testid="makers-input-country"
                placeholder="Nigeria"
                className="mt-1 w-full rounded-xl border border-stone-300 px-4 py-3 text-sm focus:border-stone-900 focus:ring-1 focus:ring-stone-900 outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-700">Region / city</label>
              <input
                type="text"
                value={form.region}
                onChange={(e) => setForm({ ...form, region: e.target.value })}
                data-testid="makers-input-region"
                placeholder="Lagos"
                className="mt-1 w-full rounded-xl border border-stone-300 px-4 py-3 text-sm focus:border-stone-900 focus:ring-1 focus:ring-stone-900 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-stone-700">Tell the story</label>
            <textarea
              required
              minLength={20}
              rows={6}
              value={form.story}
              onChange={(e) => setForm({ ...form, story: e.target.value })}
              data-testid="makers-input-story"
              placeholder="What did you build? How long did it take? What's the dream — full production line, prototype, art piece? The more honest the better."
              className="mt-1 w-full rounded-xl border border-stone-300 px-4 py-3 text-sm focus:border-stone-900 focus:ring-1 focus:ring-stone-900 outline-none resize-y"
            />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-stone-700">
              Photos (up to 6)
            </label>
            <div className="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-2">
              {imageUrls.map((u, i) => (
                <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-stone-200 border border-stone-300">
                  <img src={u} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImg(i)}
                    data-testid={`makers-image-remove-${i}`}
                    className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white hover:bg-black/80"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {imageUrls.length < 6 && (
                <label className="aspect-square rounded-xl border-2 border-dashed border-stone-300 flex items-center justify-center text-stone-500 cursor-pointer hover:border-stone-500 hover:bg-white">
                  {uploading ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <ImagePlus className="w-6 h-6" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={onFile}
                    className="hidden"
                    data-testid="makers-image-upload-input"
                  />
                </label>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-700">Video URL (optional)</label>
              <input
                type="url"
                value={form.video_url}
                onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                data-testid="makers-input-video"
                placeholder="https://youtu.be/..."
                className="mt-1 w-full rounded-xl border border-stone-300 px-4 py-3 text-sm focus:border-stone-900 focus:ring-1 focus:ring-stone-900 outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-700">Funding goal (GBP, optional)</label>
              <input
                type="number"
                min="0"
                value={form.funding_goal}
                onChange={(e) => setForm({ ...form, funding_goal: e.target.value })}
                data-testid="makers-input-funding"
                placeholder="e.g. 5000"
                className="mt-1 w-full rounded-xl border border-stone-300 px-4 py-3 text-sm focus:border-stone-900 focus:ring-1 focus:ring-stone-900 outline-none"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="makers-submit-btn"
            className="w-full py-3.5 rounded-full font-bold text-[#0A0A0A] disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: "linear-gradient(135deg, #FFB42D 0%, #FF2D7A 100%)" }}
          >
            {submitting ? "Publishing…" : "Publish my showcase"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default MakerNew;
